// webapp/src/lib/offlineFeedFallback.js
//
// Offline feed fallback. When an exact-key cache miss coincides with a
// network error, serve the NEWEST cached page for the same feed endpoint
// whose query identity (everything except limit/offset) matches. See
// docs/superpowers/specs/2026-08-09-offline-feed-design.md.
//
// Self-contained on purpose: offlineCache.js imports this module, so this
// module must not import offlineCache.js (circular import).

const FEED_ENDPOINTS = [
    '/api/articles/public/',
    '/api/articles/',
    '/api/news/public/',
];

export function getFeedConfig(endpoint) {
    return FEED_ENDPOINTS.indexOf(endpoint) !== -1;
}

// Stable JSON.stringify (key-sorted) — local copy of the offlineCache.js
// helper to stay import-free (see module header).
function stableStringify(value) {
    if (value === null || typeof value !== 'object') return JSON.stringify(value);
    if (Array.isArray(value)) {
        return '[' + value.map(stableStringify).join(',') + ']';
    }
    const keys = Object.keys(value).sort();
    return '{' + keys.map(k => JSON.stringify(k) + ':' + stableStringify(value[k])).join(',') + '}';
}

// Body fields that vary between feed call sites (loadAll, refreshInPlace,
// loadMore, prefetch) without changing WHICH feed is being requested.
// Everything else (hostname, category, sort, addData, hasRace, ...) must
// match exactly.
const PAGING_FIELDS = ['limit', 'offset'];

export function identityBodyKey(body) {
    const src = body || {};
    const identity = {};
    Object.keys(src).forEach((k) => {
        if (PAGING_FIELDS.indexOf(k) === -1) {
            identity[k] = src[k];
        }
    });
    return stableStringify(identity);
}

function isFreshEntry(entry, ttlMs) {
    if (!entry || typeof entry.savedAt !== 'number') return false;
    return (Date.now() - entry.savedAt) < ttlMs;
}

export async function findFeedFallback(storage, endpoint, userId, customerId, ttlMs, body) {
    if (!storage || !storage.getAllMatching) return null;
    const wantedIdentity = identityBodyKey(body);
    const u = userId == null ? null : String(userId);
    const c = customerId == null ? null : String(customerId);
    const matches = await storage.getAllMatching((value) => {
        if (!value || value.endpoint !== endpoint) return false;
        if (!value.body) return false; // entries written before body was stored
        if (!isFreshEntry(value, ttlMs)) return false;
        const data = value.response && value.response.data;
        if (!Array.isArray(data) || data.length === 0) return false;
        const eu = value.userId == null ? null : String(value.userId);
        const ec = value.customerId == null ? null : String(value.customerId);
        if (u != null && eu !== u) return false;
        if (c != null && ec !== c) return false;
        return identityBodyKey(value.body) === wantedIdentity;
    });
    if (!matches.length) return null;
    // Newest page wins — a feed wants the freshest snapshot, unlike the
    // search fallback's largest-list rule.
    matches.sort((a, b) => (b.value.savedAt || 0) - (a.value.savedAt || 0));
    return matches[0].value;
}

export function buildFeedFallbackResponse(entry, body) {
    if (!entry || !entry.response) return null;
    const sourceData = Array.isArray(entry.response.data) ? entry.response.data : [];
    const start = (body && Number.isFinite(body.offset)) ? body.offset : 0;
    const end = start + ((body && Number.isFinite(body.limit)) ? body.limit : sourceData.length);
    const data = sourceData.slice(start, end);
    return Object.assign({}, entry.response, {
        data,
        total: data.length,
        _fromCache: true,
    });
}
